import React, { useRef } from "react";
import "./style.scss";
import { useTranslation } from "react-i18next";
import { BasketSurveyItem } from "../../../models/Survey";
import EAButton from "../../shared/Buttons/EAButton";
import EAAlert from "../../shared/EAAlert";
import SurveyForm from "./SurveyForm";
import SurveyAssortStepper from "./components/SurveyAssortStepper";
import { MdOutlineKeyboardArrowLeft } from "react-icons/md";
import useParentMessage from "../../../../utils/hooks/useParentMessage";
import EASkeletonLoader from "../../shared/EASkeletonLoader";
import BasketSurveyAlerts from "./components/BasketSurveyAlerts";
import BasketSurveyNav from "./components/BasketSurveyNav";
import { useScrollToSurveyQuestion } from "./hooks/useScrollToSurveyQuestion";

interface IProps {
  surveyItems: BasketSurveyItem[];
  currentIndex: number;
  currentItem: BasketSurveyItem | null;
  currentAnswers: Record<string, string>;
  onAnswer: (itemIndex: number, questionId: string, value: string) => void;
  onNext: (onFinish: () => void) => boolean;
  onBack: (onBasket: () => void) => void;
  onSave: () => Promise<boolean>;
  isSaving: boolean;
  surveySaveError: boolean;
  isLoadingSurvey: boolean;
  surveyLoadError: boolean;
  invalidQuestionIds: Set<string>;
  firstInvalidQuestionId: string | null;
  surveyValidationFailSeq: number;
  onFinish: () => void;
  onBasket: () => void;
}

const BasketSurveyStep: React.FC<IProps> = ({
  surveyItems,
  currentIndex,
  currentItem,
  currentAnswers,
  onAnswer,
  onNext,
  onBack,
  onSave,
  isSaving,
  surveySaveError,
  isLoadingSurvey,
  surveyLoadError,
  invalidQuestionIds,
  firstInvalidQuestionId,
  surveyValidationFailSeq,
  onFinish,
  onBasket,
}) => {
  const { t } = useTranslation();
  const { scrollToTop } = useParentMessage();
  const topRef = useRef<HTMLDivElement>(null);
  const totalItems = surveyItems.length;
  const isLastItem = currentIndex === totalItems - 1;
  const hasValidationErrors = invalidQuestionIds.size > 0;

  useScrollToSurveyQuestion(firstInvalidQuestionId, surveyValidationFailSeq);

  const scrollUp = () => {
    scrollToTop();
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleNextClick = async () => {
    if (isLastItem) {
      const isValid = onNext(() => {});
      if (!isValid) return;
      const saved = await onSave();
      if (saved) {
        scrollUp();
        onFinish();
      }
    } else {
      const navigated = onNext(onFinish);
      if (navigated) scrollUp();
    }
  };

  if (isLoadingSurvey) {
    return (
      <div className="basket-survey-step basket-survey-step--loading" ref={topRef}>
        <EASkeletonLoader width="100%" height="300px" />
      </div>
    );
  }

  const showFetchProblem = surveyLoadError || !currentItem;

  if (showFetchProblem) {
    return (
      <div className="basket-survey-step" ref={topRef}>
        <div className="basket-survey-step__main">
          <div className="basket-survey-step__header">
            <h2 className="text-color-dark no-margins">{t("dodatkowe_informacje")}</h2>
          </div>
          <EAAlert type="error" message={t("blad_ladowania_ankiety")} />
          <div className="basket-survey-step__navigation">
            <EAButton
              title={t("poprzedni")}
              variant="outlined"
              startIcon={<MdOutlineKeyboardArrowLeft />}
              onClick={() => {
                onBasket();
                scrollUp();
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!currentItem) {
    return null;
  }

  return (
    <div className="basket-survey-step" ref={topRef}>
      <div className="basket-survey-step__main">
        <div className="basket-survey-step__header">
          <h2 className="text-color-dark no-margins">{t("dodatkowe_informacje")}</h2>
          <p className="body-text-small text-color-light-gray">{t("uzupelnij_wymagane_dane")}</p>
        </div>

        {totalItems > 1 && <SurveyAssortStepper items={surveyItems} currentIndex={currentIndex} />}

        <div className="basket-survey-step__content">
          <SurveyForm
            item={currentItem}
            answers={currentAnswers}
            onAnswer={(questionId, value) => onAnswer(currentIndex, questionId, value)}
            invalidQuestionIds={invalidQuestionIds}
          />
        </div>

        <BasketSurveyAlerts hasValidationErrors={hasValidationErrors} surveySaveError={surveySaveError} />

        <BasketSurveyNav
          isLastItem={isLastItem}
          isSaving={isSaving}
          onBackClick={() => {
            onBack(onBasket);
            scrollUp();
          }}
          onNextClick={handleNextClick}
        />
      </div>
    </div>
  );
};

export default BasketSurveyStep;
